import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { createClient } from '@supabase/supabase-js'
import SalaVideos from './SalaVideos'

const API = process.env.REACT_APP_API_URL
const NIVELES_ORDEN = ['PRE', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']

// Mismo patron que SalaGrabacion.jsx: el navegador sube el archivo
// directamente a Supabase Storage (bucket "fotos-profesores", debe
// crearse como bucket publico) y solo la URL publica resultante se
// guarda en la base de datos.
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

async function subirFotoProfesor(archivo) {
  const ext = (archivo.name?.split('.').pop() || 'jpg').toLowerCase()
  const nombreArchivo = 'profesor_' + Date.now() + '_' + Math.random().toString(36).slice(2) + '.' + ext
  const { error } = await supabase.storage
    .from('fotos-profesores')
    .upload(nombreArchivo, archivo, { contentType: archivo.type })
  if (error) throw error
  const { data } = supabase.storage.from('fotos-profesores').getPublicUrl(nombreArchivo)
  return data.publicUrl
}

function cabeceras(usuario) {
  return { headers: { 'x-usuario-id': usuario?.id } }
}

function formatoFechaCorta(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
}

function formatoFechaHora(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

const estilos = {
  tab: activo => ({
    padding: '9px 18px', borderRadius: 10, border: `2px solid ${activo ? '#1B3A8C' : '#E2E6F0'}`,
    background: activo ? '#1B3A8C' : '#fff', color: activo ? '#fff' : '#374151',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer'
  }),
  card: { background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, padding: 18, marginBottom: 14 },
  label: { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E2E6F0', fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' },
  boton: (color = '#1B3A8C') => ({ padding: '9px 18px', borderRadius: 10, border: 'none', background: color, color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }),
  botonSec: { padding: '9px 18px', borderRadius: 10, border: '1.5px solid #E2E6F0', background: '#fff', color: '#374151', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 800, color: '#6B7280', textTransform: 'uppercase', borderBottom: '2px solid #E2E6F0' },
  td: { padding: '10px', fontSize: 13, borderBottom: '1px solid #F1F3F9', color: '#1A1A2E' }
}

// ══════════════════════════════════════ TAB: PROFESORES ═══
function TabProfesores({ usuario, setMensaje }) {
  const [profesores, setProfesores] = useState([])
  const [horasPorProfesor, setHorasPorProfesor] = useState({})
  const [cargando, setCargando] = useState(true)
  const [editando, setEditando] = useState(null) // null = formulario cerrado, {} = nuevo, {...} = editar
  const [niveles] = useState(NIVELES_ORDEN)

  const cargar = useCallback(() => {
    setCargando(true)
    Promise.all([
      axios.get(`${API}/clases/admin/profesores`, cabeceras(usuario)),
      // Horas reales dictadas (segun cuando se inicio y termino la sala de
      // video de cada clase) -- si falla no bloqueamos la pantalla, la
      // columna simplemente queda en 0.
      axios.get(`${API}/clases/admin/horas-profesor`, cabeceras(usuario)).catch(() => ({ data: { datos: [] } }))
    ]).then(([p, h]) => {
      setProfesores(p.data.datos || [])
      setHorasPorProfesor(Object.fromEntries((h.data.datos || []).map(x => [x.profesor_id, x])))
    }).catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los profesores.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])

  useEffect(() => { cargar() }, [cargar])

  async function guardar(form) {
    try {
      if (form.id) {
        await axios.put(`${API}/clases/admin/profesores/${form.id}`, form, cabeceras(usuario))
      } else {
        await axios.post(`${API}/clases/admin/profesores`, form, cabeceras(usuario))
      }
      setMensaje({ tipo: 'ok', texto: 'Profesor guardado.' })
      setEditando(null)
      cargar()
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar el profesor.' })
    }
  }

  function alternarActivo(p) {
    axios.put(`${API}/clases/admin/profesores/${p.id}`, { activo: !p.activo }, cabeceras(usuario))
      .then(cargar)
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo actualizar el profesor.' }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button style={estilos.boton()} onClick={() => setEditando({ nombre: '', email: '', bio: '', avatar_emoji: '🧑‍🏫', niveles: [], cuenta_email: '' })}>
          + Nuevo profesor
        </button>
      </div>

      {editando && (
        <FormProfesor inicial={editando} niveles={niveles} onCancelar={() => setEditando(null)} onGuardar={guardar} />
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={estilos.th}>Profesor</th>
              <th style={estilos.th}>Email</th>
              <th style={estilos.th}>Niveles</th>
              <th style={estilos.th}>Cuenta de login</th>
              <th style={estilos.th}>Estado</th>
              <th style={estilos.th}>Horas dictadas</th>
              <th style={estilos.th}></th>
            </tr>
          </thead>
          <tbody>
            {profesores.map(p => {
              const horas = horasPorProfesor[p.id]
              return (
              <tr key={p.id}>
                <td style={estilos.td}>
                  {p.foto_url
                    ? <img src={p.foto_url} alt={p.nombre} style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', verticalAlign: 'middle', marginRight: 6 }} />
                    : <span style={{ marginRight: 6 }}>{p.avatar_emoji}</span>}
                  {p.nombre}
                </td>
                <td style={estilos.td}>{p.email || '—'}</td>
                <td style={estilos.td}>{(p.niveles || []).join(', ') || '—'}</td>
                <td style={estilos.td}>
                  {p.cuenta?.email
                    ? <span style={{ color: '#166534' }}>✓ {p.cuenta.email}</span>
                    : <span style={{ color: '#9CA3AF' }}>Sin vincular</span>}
                </td>
                <td style={estilos.td}>
                  <span style={{ color: p.activo ? '#166534' : '#991B1B', fontWeight: 700 }}>
                    {p.activo ? '● Activo' : '○ Inactivo'}
                  </span>
                </td>
                <td style={estilos.td}>
                  {horas
                    ? <span style={{ fontWeight: 700 }}>{horas.horas_totales}h <span style={{ color: '#6B7280', fontWeight: 400 }}>({horas.clases_dictadas} clases)</span></span>
                    : <span style={{ color: '#9CA3AF' }}>—</span>}
                </td>
                <td style={estilos.td}>
                  <button style={{ ...estilos.botonSec, marginRight: 8, padding: '5px 12px' }} onClick={() => setEditando({ ...p, cuenta_email: p.cuenta?.email || '' })}>Editar</button>
                  <button style={{ ...estilos.botonSec, padding: '5px 12px' }} onClick={() => alternarActivo(p)}>
                    {p.activo ? 'Desactivar' : 'Activar'}
                  </button>
                </td>
              </tr>
              )
            })}
            {profesores.length === 0 && (
              <tr><td colSpan={7} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no hay profesores.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

function FormProfesor({ inicial, niveles, onCancelar, onGuardar }) {
  const [form, setForm] = useState(inicial)
  const [subiendoFoto, setSubiendoFoto] = useState(false)
  function alternarNivel(n) {
    setForm(f => ({ ...f, niveles: f.niveles.includes(n) ? f.niveles.filter(x => x !== n) : [...f.niveles, n] }))
  }
  async function elegirFoto(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setSubiendoFoto(true)
    try {
      const url = await subirFotoProfesor(archivo)
      setForm(f => ({ ...f, foto_url: url }))
    } catch (err) {
      alert('No se pudo subir la foto. Intenta de nuevo.')
    }
    setSubiendoFoto(false)
    e.target.value = ''
  }
  return (
    <div style={estilos.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#F4F6FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, overflow: 'hidden', flexShrink: 0 }}>
          {form.foto_url ? <img src={form.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (form.avatar_emoji || '🧑‍🏫')}
        </div>
        <div>
          <label style={{ ...estilos.botonSec, display: 'inline-block', padding: '7px 14px', fontSize: 12 }}>
            {subiendoFoto ? 'Subiendo...' : (form.foto_url ? '📷 Cambiar foto' : '📷 Subir foto')}
            <input type="file" accept="image/*" onChange={elegirFoto} disabled={subiendoFoto} style={{ display: 'none' }} />
          </label>
          <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4, marginBottom: 0 }}>Se muestra al estudiante en vez del emoji, si hay una foto.</p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={estilos.label}>Nombre</label>
          <input style={estilos.input} value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Teacher Sarah" />
        </div>
        <div>
          <label style={estilos.label}>Email</label>
          <input style={estilos.input} value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="sarah@englishbridge.com" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={estilos.label}>Bio</label>
          <input style={estilos.input} value={form.bio || ''} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="Breve descripcion" />
        </div>
        <div>
          <label style={estilos.label}>Emoji</label>
          <input style={estilos.input} value={form.avatar_emoji || ''} onChange={e => setForm({ ...form, avatar_emoji: e.target.value })} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={estilos.label}>Correo de la cuenta de login (opcional)</label>
        <input style={estilos.input} value={form.cuenta_email || ''} onChange={e => setForm({ ...form, cuenta_email: e.target.value })}
          placeholder="El correo con el que el profesor entra a EnglishBridge (rol Profesor)" />
        <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
          Vincula esta ficha a la cuenta con la que el profesor inicia sesión (debe haberse registrado antes eligiendo "Profesor" en el login). Así puede entrar a sus propias clases sin necesitar acceso de administrador. Déjalo vacío para no vincular ninguna cuenta.
        </p>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={estilos.label}>Niveles que puede enseñar</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {niveles.map(n => (
            <button key={n} onClick={() => alternarNivel(n)} type="button"
              style={{ padding: '5px 12px', borderRadius: 999, border: `1.5px solid ${form.niveles.includes(n) ? '#1B3A8C' : '#E2E6F0'}`, background: form.niveles.includes(n) ? '#1B3A8C' : '#fff', color: form.niveles.includes(n) ? '#fff' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {n}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={estilos.boton()} onClick={() => onGuardar(form)} disabled={!form.nombre}>Guardar</button>
        <button style={estilos.botonSec} onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════ TAB: CLASES ════════
// Cada clase es un evento puntual (fecha y hora exactas), no un
// horario recurrente — el admin la crea una por una, como en
// EspañolGo: eliges dia, hora, duracion, y puedes poner requisitos.
const ROTULO_SALA = {
  inactiva: { texto: 'Sin iniciar', color: '#6B7280' },
  en_vivo: { texto: '● En vivo', color: '#166534' },
  finalizada: { texto: 'Finalizada', color: '#6B7280' }
}
const ROTULO_GRABACION = {
  sin_grabar: null,
  grabando: '🔴 Grabando',
  procesando: '⏳ Procesando',
  lista: '✅ Grabación lista',
  error: '⚠ Error al grabar'
}

function TabClases({ usuario, setMensaje, onIniciarClase }) {
  const [clases, setClases] = useState([])
  const [profesores, setProfesores] = useState([])
  const [niveles, setNiveles] = useState([])
  const [cargando, setCargando] = useState(true)
  const [mostrarForm, setMostrarForm] = useState(false)
  const vacio = { profesor_id: '', tipo_clase: 'grupal', titulo: '', requisitos: '', fecha: '', hora_inicio: '18:00', duracion_min: 60, nivel_id: '', cupo_maximo: 10 }
  const [form, setForm] = useState(vacio)

  const cargar = useCallback(() => {
    setCargando(true)
    Promise.all([
      axios.get(`${API}/clases/admin/clases`, cabeceras(usuario)),
      axios.get(`${API}/clases/admin/profesores`, cabeceras(usuario)),
      axios.get(`${API}/curriculo/niveles`)
    ]).then(([c, p, n]) => {
      setClases(c.data.datos || [])
      setProfesores(p.data.datos || [])
      setNiveles(n.data.datos || [])
    }).catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar las clases.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])

  useEffect(() => { cargar() }, [cargar])

  async function crear() {
    if (!form.profesor_id) { setMensaje({ tipo: 'error', texto: 'Elige un profesor.' }); return }
    if (!form.fecha) { setMensaje({ tipo: 'error', texto: 'Elige la fecha de la clase.' }); return }
    try {
      await axios.post(`${API}/clases/admin/clases`, {
        ...form,
        duracion_min: Number(form.duracion_min),
        cupo_maximo: Number(form.cupo_maximo),
        nivel_id: form.tipo_clase === 'grupal' ? (form.nivel_id || null) : null
      }, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Clase creada.' })
      setForm(vacio)
      setMostrarForm(false)
      cargar()
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo crear la clase.' })
    }
  }

  function cancelarClase(c) {
    axios.put(`${API}/clases/admin/clases/${c.id}`, { activo: !c.activo }, cabeceras(usuario))
      .then(cargar)
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo actualizar la clase.' }))
  }

  function eliminarClase(c) {
    const confirmado = window.confirm(
      `¿Eliminar "${c.titulo || 'esta clase'}" para siempre? Esto también borra las reservas de los estudiantes a esta clase. No se puede deshacer.`
    )
    if (!confirmado) return
    axios.delete(`${API}/clases/admin/clases/${c.id}`, cabeceras(usuario))
      .then(() => { setMensaje({ tipo: 'ok', texto: 'Clase eliminada.' }); cargar() })
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo eliminar la clase.' }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button style={estilos.boton()} onClick={() => setMostrarForm(m => !m)}>
          {mostrarForm ? 'Cerrar formulario' : '+ Nueva clase'}
        </button>
      </div>

      {mostrarForm && (
        <div style={estilos.card}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={estilos.label}>Profesor</label>
              <select style={estilos.input} value={form.profesor_id} onChange={e => setForm({ ...form, profesor_id: e.target.value })}>
                <option value="">Elige un profesor</option>
                {profesores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={estilos.label}>Tipo de clase</label>
              <select style={estilos.input} value={form.tipo_clase} onChange={e => setForm({ ...form, tipo_clase: e.target.value })}>
                <option value="grupal">Grupal</option>
                <option value="privada">Privada</option>
              </select>
            </div>
            <div>
              <label style={estilos.label}>Titulo (opcional)</label>
              <input style={estilos.input} value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Practica de Present Perfect" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={estilos.label}>Fecha</label>
              <input type="date" style={estilos.input} value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div>
              <label style={estilos.label}>Hora de inicio</label>
              <input type="time" style={estilos.input} value={form.hora_inicio} onChange={e => setForm({ ...form, hora_inicio: e.target.value })} />
            </div>
            <div>
              <label style={estilos.label}>Duracion (min)</label>
              <input type="number" style={estilos.input} value={form.duracion_min} onChange={e => setForm({ ...form, duracion_min: e.target.value })} />
            </div>
            {form.tipo_clase === 'grupal' && (
              <div>
                <label style={estilos.label}>Nivel</label>
                <select style={estilos.input} value={form.nivel_id} onChange={e => setForm({ ...form, nivel_id: e.target.value })}>
                  <option value="">Cualquier nivel</option>
                  {niveles.map(n => <option key={n.id} value={n.id}>{n.nombre} ({n.id})</option>)}
                </select>
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: form.tipo_clase === 'grupal' ? '1fr 160px' : '1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={estilos.label}>Requisitos / notas para el estudiante (opcional)</label>
              <input style={estilos.input} value={form.requisitos} onChange={e => setForm({ ...form, requisitos: e.target.value })} placeholder="Ej: Nivel A1 en adelante, traer audifonos" />
            </div>
            {form.tipo_clase === 'grupal' && (
              <div>
                <label style={estilos.label}>Cupo maximo</label>
                <input type="number" style={estilos.input} value={form.cupo_maximo} onChange={e => setForm({ ...form, cupo_maximo: e.target.value })} />
              </div>
            )}
          </div>
          <button style={estilos.boton()} onClick={crear}>Crear clase</button>
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={estilos.th}>Clase</th>
              <th style={estilos.th}>Profesor</th>
              <th style={estilos.th}>Tipo</th>
              <th style={estilos.th}>Fecha / Hora</th>
              <th style={estilos.th}>Nivel</th>
              <th style={estilos.th}>Cupo</th>
              <th style={estilos.th}>Estado</th>
              <th style={estilos.th}>Video</th>
              <th style={estilos.th}></th>
            </tr>
          </thead>
          <tbody>
            {clases.map(c => (
              <tr key={c.id}>
                <td style={estilos.td}>{c.titulo || '—'}</td>
                <td style={estilos.td}>{c.profesor?.avatar_emoji} {c.profesor?.nombre}</td>
                <td style={estilos.td}>{c.tipo_clase === 'grupal' ? 'Grupal' : 'Privada'}</td>
                <td style={estilos.td}>{formatoFechaCorta(c.fecha)} · {c.hora_inicio?.slice(0, 5)}</td>
                <td style={estilos.td}>{c.nivel_id || '—'}</td>
                <td style={estilos.td}>{c.tipo_clase === 'grupal' ? c.cupo_maximo : '1 a 1'}</td>
                <td style={estilos.td}>
                  <span style={{ color: c.activo ? '#166534' : '#991B1B', fontWeight: 700 }}>
                    {c.activo ? '● Activa' : '○ Cancelada'}
                  </span>
                </td>
                <td style={estilos.td}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button style={{ ...estilos.boton(), padding: '5px 12px' }} onClick={() => onIniciarClase(c.id)} disabled={!c.activo}>
                      🎥 {c.sala_estado === 'en_vivo' ? 'Entrar' : 'Iniciar clase'}
                    </button>
                    <span style={{ fontSize: 11, fontWeight: 700, color: (ROTULO_SALA[c.sala_estado] || ROTULO_SALA.inactiva).color }}>
                      {(ROTULO_SALA[c.sala_estado] || ROTULO_SALA.inactiva).texto}
                    </span>
                    {ROTULO_GRABACION[c.grabacion_estado] && (
                      <span style={{ fontSize: 11, color: '#8A6D1D' }}>{ROTULO_GRABACION[c.grabacion_estado]}</span>
                    )}
                    {c.grabacion_url && (
                      <a href={c.grabacion_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#1B3A8C', fontWeight: 700 }}>▶ Ver grabación</a>
                    )}
                  </div>
                </td>
                <td style={estilos.td}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button style={{ ...estilos.botonSec, padding: '5px 12px' }} onClick={() => cancelarClase(c)}>
                      {c.activo ? 'Cancelar' : 'Reactivar'}
                    </button>
                    <button style={{ ...estilos.botonSec, padding: '5px 12px', color: '#991B1B', borderColor: '#FCA5A5' }} onClick={() => eliminarClase(c)}>
                      🗑 Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {clases.length === 0 && (
              <tr><td colSpan={9} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no has creado ninguna clase.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ══════════════════════════════════════ TAB: RESERVAS ══════
function TabReservas({ usuario, setMensaje }) {
  const [reservas, setReservas] = useState([])
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    setCargando(true)
    axios.get(`${API}/clases/admin/reservas`, cabeceras(usuario))
      .then(r => setReservas(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar las reservas.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])

  if (cargando) return <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={estilos.th}>Estudiante</th>
          <th style={estilos.th}>Clase</th>
          <th style={estilos.th}>Profesor</th>
          <th style={estilos.th}>Tipo</th>
          <th style={estilos.th}>Fecha</th>
          <th style={estilos.th}>Hora</th>
          <th style={estilos.th}>Estado</th>
          <th style={estilos.th}>Asistió</th>
          <th style={estilos.th}>Reservado el</th>
        </tr>
      </thead>
      <tbody>
        {reservas.map(r => (
          <tr key={r.id}>
            <td style={estilos.td}>{r.usuario_email || r.usuario_id}</td>
            <td style={estilos.td}>{r.clase?.titulo || '—'}</td>
            <td style={estilos.td}>{r.clase?.profesor?.avatar_emoji} {r.clase?.profesor?.nombre}</td>
            <td style={estilos.td}>{r.tipo_clase === 'grupal' ? 'Grupal' : 'Privada'}</td>
            <td style={estilos.td}>{formatoFechaCorta(r.fecha)}</td>
            <td style={estilos.td}>{r.clase?.hora_inicio?.slice(0, 5)}</td>
            <td style={estilos.td}>
              <span style={{ color: r.estado === 'reservada' ? '#166534' : '#991B1B', fontWeight: 700 }}>
                {r.estado === 'reservada' ? '● Reservada' : r.estado === 'cancelada' ? '○ Cancelada' : r.estado}
              </span>
            </td>
            <td style={estilos.td}>
              {/* Se marca solo, en el momento en que el estudiante entra de
                  verdad a la sala de video de la clase (ver videollamada.js
                  /token) -- no es algo que el admin tenga que llenar. */}
              {r.asistio
                ? <span style={{ color: '#166534', fontWeight: 700 }}>✓ Sí{r.asistio_en ? ` · ${formatoFechaHora(r.asistio_en)}` : ''}</span>
                : <span style={{ color: '#9CA3AF' }}>— No</span>}
            </td>
            <td style={estilos.td}>{formatoFechaHora(r.creado_en)}</td>
          </tr>
        ))}
        {reservas.length === 0 && (
          <tr><td colSpan={9} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no hay reservas.</td></tr>
        )}
      </tbody>
    </table>
  )
}

// ══════════════════════════════════════ TAB: VIDEOFOROS ════
// Mismo concepto que ya existe en EspañolGo (PanelAdmin.jsx, seccion
// "foros"): reuniones grupales en vivo sobre un tema puntual, distintas
// de las Clases (que siguen el plan de estudio). Sigue exactamente el
// mismo patron de formulario/tabla que TabClases de arriba.
// Los VideoForos se reunen por un link externo (Meet/Zoom), asi que a
// diferencia de las Clases no hay forma de detectar solo quien entro --
// aqui el profesor/admin marca a mano, despues del foro, quien asistio de
// verdad entre los inscritos.
function ModalAsistenciaForo({ foro, usuario, onCerrar, setMensaje }) {
  const [inscritos, setInscritos] = useState([])
  const [cargando, setCargando] = useState(true)

  const cargar = useCallback(() => {
    setCargando(true)
    axios.get(`${API}/videoforos/admin/${foro.id}/inscritos`, cabeceras(usuario))
      .then(r => setInscritos(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo cargar la lista de inscritos.' }))
      .finally(() => setCargando(false))
  }, [foro.id, usuario, setMensaje])

  useEffect(() => { cargar() }, [cargar])

  function alternar(inscripcion) {
    axios.put(`${API}/videoforos/admin/inscripciones/${inscripcion.id}/asistencia`,
      { asistio: !inscripcion.asistio }, cabeceras(usuario))
      .then(cargar)
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo actualizar la asistencia.' }))
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={onCerrar}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 22, width: 440, maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800 }}>Asistencia · {foro.titulo}</h3>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6B7280' }}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>Marca quien de los inscritos asistió de verdad al foro.</p>
        {cargando ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#6B7280' }}>Cargando...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {inscritos.map(i => (
              <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, background: '#F8FAFF', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!i.asistio} onChange={() => alternar(i)} />
                <span style={{ fontSize: 13 }}>{i.email || i.usuario_id}</span>
              </label>
            ))}
            {inscritos.length === 0 && (
              <div style={{ textAlign: 'center', padding: 16, color: '#6B7280', fontSize: 13 }}>Nadie se ha inscrito a este foro.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function TabVideoForos({ usuario, setMensaje }) {
  const [foros, setForos] = useState([])
  const [profesores, setProfesores] = useState([])
  const [niveles, setNiveles] = useState([])
  const [cargando, setCargando] = useState(true)
  const [mostrarForm, setMostrarForm] = useState(false)
  const vacio = { profesor_id: '', titulo: '', descripcion: '', tema: '', nivel_id: '', fecha: '', hora_inicio: '18:00', hora_fin: '', duracion_min: 75, cupo_maximo: 10, link_meet: '' }
  const [form, setForm] = useState(vacio)
  const [editandoId, setEditandoId] = useState(null)
  const [verAsistencia, setVerAsistencia] = useState(null) // foro seleccionado, o null

  const cargar = useCallback(() => {
    setCargando(true)
    Promise.all([
      axios.get(`${API}/videoforos/admin/todos`, cabeceras(usuario)),
      axios.get(`${API}/clases/admin/profesores`, cabeceras(usuario)),
      axios.get(`${API}/curriculo/niveles`)
    ]).then(([f, p, n]) => {
      setForos(f.data.datos || [])
      setProfesores(p.data.datos || [])
      setNiveles(n.data.datos || [])
    }).catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los VideoForos.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])

  useEffect(() => { cargar() }, [cargar])

  async function guardar() {
    if (!form.titulo) { setMensaje({ tipo: 'error', texto: 'Escribe un titulo para el VideoForo.' }); return }
    if (!form.fecha) { setMensaje({ tipo: 'error', texto: 'Elige la fecha del VideoForo.' }); return }
    try {
      const cuerpo = {
        ...form,
        profesor_id: form.profesor_id || null,
        nivel_id: form.nivel_id || null,
        hora_fin: form.hora_fin || null,
        duracion_min: Number(form.duracion_min),
        cupo_maximo: Number(form.cupo_maximo)
      }
      if (editandoId) {
        await axios.put(`${API}/videoforos/admin/${editandoId}`, cuerpo, cabeceras(usuario))
        setMensaje({ tipo: 'ok', texto: 'VideoForo actualizado.' })
      } else {
        await axios.post(`${API}/videoforos/admin`, cuerpo, cabeceras(usuario))
        setMensaje({ tipo: 'ok', texto: 'VideoForo creado.' })
      }
      setForm(vacio)
      setEditandoId(null)
      setMostrarForm(false)
      cargar()
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar el VideoForo.' })
    }
  }

  function editar(f) {
    setForm({
      profesor_id: f.profesor_id || '', titulo: f.titulo || '', descripcion: f.descripcion || '',
      tema: f.tema || '', nivel_id: f.nivel_id || '', fecha: f.fecha || '',
      hora_inicio: f.hora_inicio?.slice(0, 5) || '18:00', hora_fin: f.hora_fin?.slice(0, 5) || '',
      duracion_min: f.duracion_min || 75, cupo_maximo: f.cupo_maximo || 10, link_meet: f.link_meet || ''
    })
    setEditandoId(f.id)
    setMostrarForm(true)
  }

  function alternarActivo(f) {
    axios.put(`${API}/videoforos/admin/${f.id}`, { activo: !f.activo }, cabeceras(usuario))
      .then(cargar)
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo actualizar el VideoForo.' }))
  }

  function eliminar(f) {
    const confirmado = window.confirm(`¿Eliminar "${f.titulo}" para siempre? Esto tambien borra las inscripciones de los estudiantes. No se puede deshacer.`)
    if (!confirmado) return
    axios.delete(`${API}/videoforos/admin/${f.id}`, cabeceras(usuario))
      .then(() => { setMensaje({ tipo: 'ok', texto: 'VideoForo eliminado.' }); cargar() })
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo eliminar el VideoForo.' }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button style={estilos.boton()} onClick={() => { setForm(vacio); setEditandoId(null); setMostrarForm(m => !m) }}>
          {mostrarForm ? 'Cerrar formulario' : '+ Nuevo VideoForo'}
        </button>
      </div>

      {mostrarForm && (
        <div style={estilos.card}>
          <div style={{ marginBottom: 12 }}>
            <label style={estilos.label}>Titulo del foro</label>
            <input style={estilos.input} value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Charla sobre viajes y turismo" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={estilos.label}>Descripcion</label>
            <input style={estilos.input} value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder="De que tratara el foro..." />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={estilos.label}>Tema / categoria</label>
              <input style={estilos.input} value={form.tema} onChange={e => setForm({ ...form, tema: e.target.value })} placeholder="Ej: Viajes y turismo" />
            </div>
            <div>
              <label style={estilos.label}>Nivel</label>
              <select style={estilos.input} value={form.nivel_id} onChange={e => setForm({ ...form, nivel_id: e.target.value })}>
                <option value="">Cualquier nivel</option>
                {niveles.map(n => <option key={n.id} value={n.id}>{n.nombre} ({n.id})</option>)}
              </select>
            </div>
            <div>
              <label style={estilos.label}>Moderador</label>
              <select style={estilos.input} value={form.profesor_id} onChange={e => setForm({ ...form, profesor_id: e.target.value })}>
                <option value="">Sin moderador asignado</option>
                {profesores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={estilos.label}>Fecha</label>
              <input type="date" style={estilos.input} value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div>
              <label style={estilos.label}>Hora inicio</label>
              <input type="time" style={estilos.input} value={form.hora_inicio} onChange={e => setForm({ ...form, hora_inicio: e.target.value })} />
            </div>
            <div>
              <label style={estilos.label}>Hora fin</label>
              <input type="time" style={estilos.input} value={form.hora_fin} onChange={e => setForm({ ...form, hora_fin: e.target.value })} />
            </div>
            <div>
              <label style={estilos.label}>Cupo maximo</label>
              <input type="number" min={1} style={estilos.input} value={form.cupo_maximo} onChange={e => setForm({ ...form, cupo_maximo: e.target.value })} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={estilos.label}>Link de la reunion (Google Meet, Zoom, etc.)</label>
            <input style={estilos.input} value={form.link_meet} onChange={e => setForm({ ...form, link_meet: e.target.value })} placeholder="https://meet.google.com/..." />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={estilos.boton()} onClick={guardar}>{editandoId ? 'Actualizar VideoForo' : 'Crear VideoForo'}</button>
            <button style={estilos.botonSec} onClick={() => { setMostrarForm(false); setForm(vacio); setEditandoId(null) }}>Cancelar</button>
          </div>
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={estilos.th}>VideoForo</th>
              <th style={estilos.th}>Moderador</th>
              <th style={estilos.th}>Fecha / Hora</th>
              <th style={estilos.th}>Nivel</th>
              <th style={estilos.th}>Cupos</th>
              <th style={estilos.th}>Estado</th>
              <th style={estilos.th}></th>
            </tr>
          </thead>
          <tbody>
            {foros.map(f => (
              <tr key={f.id}>
                <td style={estilos.td}>{f.titulo}</td>
                <td style={estilos.td}>{f.profesor?.avatar_emoji} {f.profesor?.nombre || '—'}</td>
                <td style={estilos.td}>{formatoFechaCorta(f.fecha)} · {f.hora_inicio?.slice(0, 5)}</td>
                <td style={estilos.td}>{f.nivel_id || 'Cualquiera'}</td>
                <td style={estilos.td}>{f.cupos_ocupados || 0} / {f.cupo_maximo}</td>
                <td style={estilos.td}>
                  <span style={{ color: f.activo ? '#166534' : '#991B1B', fontWeight: 700 }}>
                    {f.activo ? '● Activo' : '○ Cancelado'}
                  </span>
                </td>
                <td style={estilos.td}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button style={{ ...estilos.botonSec, padding: '5px 12px' }} onClick={() => setVerAsistencia(f)}>✓ Asistencia</button>
                    <button style={{ ...estilos.botonSec, padding: '5px 12px' }} onClick={() => editar(f)}>Editar</button>
                    <button style={{ ...estilos.botonSec, padding: '5px 12px' }} onClick={() => alternarActivo(f)}>
                      {f.activo ? 'Cancelar' : 'Reactivar'}
                    </button>
                    <button style={{ ...estilos.botonSec, padding: '5px 12px', color: '#991B1B', borderColor: '#FCA5A5' }} onClick={() => eliminar(f)}>
                      🗑 Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {foros.length === 0 && (
              <tr><td colSpan={7} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no has creado ningun VideoForo.</td></tr>
            )}
          </tbody>
        </table>
      )}
      {verAsistencia && (
        <ModalAsistenciaForo foro={verAsistencia} usuario={usuario} setMensaje={setMensaje} onCerrar={() => setVerAsistencia(null)} />
      )}
    </div>
  )
}

function formatoCOP(valor) {
  if (valor === null || valor === undefined) return '—'
  return '$' + Number(valor).toLocaleString('es-CO') + ' COP'
}

// ══════════════════════════════════════ TAB: PAGOS ═══
// Antes, para ver cuanto se habia facturado o quien habia pagado, habia
// que entrar directo a Supabase — esta pestaña trae esos mismos datos
// (la tabla "pagos" ya existia en el backend, solo no se mostraba en
// ningun lado del panel).
function TabPagos({ usuario, setMensaje }) {
  const [pagos, setPagos] = useState([])
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    setCargando(true)
    axios.get(`${API}/pagos/admin/pagos`, cabeceras(usuario))
      .then(r => setPagos(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los pagos.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])

  if (cargando) return <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>

  const aprobados = pagos.filter(p => p.estado === 'APPROVED')
  // p.monto viene en CENTAVOS desde el backend (asi lo exige la formula de
  // integridad de Wompi — ver api/src/routes/pagos.js). Antes se mostraba
  // ese numero crudo como si fueran pesos (100 veces mas de lo real: un
  // plan de $59.900 aparecia como $5.990.000). Se divide entre 100 aqui,
  // igual que ya se hacia en los correos de confirmacion.
  const totalIngresos = aprobados.reduce((suma, p) => suma + (p.monto || 0), 0) / 100

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ ...estilos.card, flex: '1 1 200px', marginBottom: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, marginBottom: 6 }}>INGRESOS TOTALES</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#166534' }}>{formatoCOP(totalIngresos)}</div>
        </div>
        <div style={{ ...estilos.card, flex: '1 1 200px', marginBottom: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, marginBottom: 6 }}>PAGOS APROBADOS</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#1B3A8C' }}>{aprobados.length}</div>
        </div>
        <div style={{ ...estilos.card, flex: '1 1 200px', marginBottom: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, marginBottom: 6 }}>TOTAL DE REGISTROS</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#374151' }}>{pagos.length}</div>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={estilos.th}>Fecha</th>
            <th style={estilos.th}>Estudiante</th>
            <th style={estilos.th}>Plan</th>
            <th style={estilos.th}>Monto</th>
            <th style={estilos.th}>Método</th>
            <th style={estilos.th}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {pagos.map(p => (
            <tr key={p.id}>
              <td style={estilos.td}>{formatoFechaHora(p.creado_en)}</td>
              <td style={estilos.td}>{p.usuario?.email || p.usuario_id}</td>
              <td style={estilos.td}>{p.plan_nombre}</td>
              <td style={estilos.td}>{formatoCOP(p.monto != null ? p.monto / 100 : null)}</td>
              <td style={estilos.td}>{p.metodo || '—'}</td>
              <td style={estilos.td}>
                <span style={{
                  color: p.estado === 'APPROVED' ? '#166534' : p.estado === 'DECLINED' ? '#991B1B' : '#8A6D1D',
                  fontWeight: 700
                }}>
                  {p.estado}
                </span>
              </td>
            </tr>
          ))}
          {pagos.length === 0 && (
            <tr><td colSpan={6} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no hay pagos registrados.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ══════════════════════════════════════ TAB: ESTUDIANTES ═══
// Progreso y plan de cada estudiante en una sola tabla — antes tampoco
// existia esta vista, solo se podia consultar estudiante por estudiante
// entrando directo a la base de datos.
function TabEstudiantes({ usuario, setMensaje }) {
  const [estudiantes, setEstudiantes] = useState([])
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    setCargando(true)
    axios.get(`${API}/progreso/admin/estudiantes`, cabeceras(usuario))
      .then(r => setEstudiantes(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo cargar el progreso de los estudiantes.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])

  if (cargando) return <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>

  const ESTADO_COLOR = { activa: '#166534', prueba: '#1B3A8C', vencida: '#991B1B', sin_plan: '#6B7280' }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={estilos.th}>Estudiante</th>
          <th style={estilos.th}>Plan</th>
          <th style={estilos.th}>Estado</th>
          <th style={estilos.th}>Vence</th>
          <th style={estilos.th}>Nivel</th>
          <th style={estilos.th}>XP</th>
          <th style={estilos.th}>Lecciones</th>
          <th style={estilos.th}>Racha</th>
          <th style={estilos.th}>Último día activo</th>
        </tr>
      </thead>
      <tbody>
        {estudiantes.map(e => (
          <tr key={e.usuario_id}>
            <td style={estilos.td}>{e.email}</td>
            <td style={estilos.td}>{e.plan_actual || '—'}</td>
            <td style={estilos.td}>
              <span style={{ color: ESTADO_COLOR[e.suscripcion_estado] || '#6B7280', fontWeight: 700 }}>
                {e.suscripcion_estado}
              </span>
            </td>
            <td style={estilos.td}>{e.suscripcion_vence ? formatoFechaHora(e.suscripcion_vence) : '—'}</td>
            <td style={estilos.td}>{e.nivel_actual}</td>
            <td style={estilos.td}>{e.xp_total}</td>
            <td style={estilos.td}>{e.lecciones_completadas}</td>
            <td style={estilos.td}>{e.racha_dias} 🔥</td>
            <td style={estilos.td}>{e.ultimo_dia_activo ? formatoFechaCorta(e.ultimo_dia_activo) : '—'}</td>
          </tr>
        ))}
        {estudiantes.length === 0 && (
          <tr><td colSpan={9} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no hay estudiantes registrados.</td></tr>
        )}
      </tbody>
    </table>
  )
}

// ══════════════════════════════════════ TAB: EXAMEN DE NIVEL ═══
// Antes, la escritura y los audios de speaking del examen de nivel SOLO
// llegaban por correo — no quedaba ningun registro dentro de la
// plataforma, y era facil perder el hilo de a quien ya se le habia
// respondido. Esta pestaña trae esa cola de revision aqui mismo.
function FilaExamen({ examen, usuario, setMensaje, onCalificado }) {
  const [nivelFinal, setNivelFinal] = useState(examen.nivel_final || '')
  const [notas, setNotas] = useState(examen.notas_admin || '')
  const [guardando, setGuardando] = useState(false)
  const soloLectura = examen.estado === 'revisado'

  async function calificar() {
    if (!nivelFinal) { setMensaje({ tipo: 'error', texto: 'Elige un nivel final antes de guardar.' }); return }
    setGuardando(true)
    try {
      await axios.post(`${API}/examen-revision/admin/${examen.id}/calificar`,
        { nivel_final: nivelFinal, notas_admin: notas }, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Examen calificado.' })
      onCalificado()
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar la calificación.' })
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div style={estilos.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{examen.email}</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            Nivel estimado (auto): {examen.nivel_estimado || 'N/A'} · Recibido: {formatoFechaHora(examen.creado_en)}
          </div>
        </div>
        {soloLectura && (
          <div style={{ fontSize: 13, fontWeight: 800, color: '#166534' }}>
            ✅ Nivel final: {examen.nivel_final}
          </div>
        )}
      </div>

      {(examen.escritura || []).map((e, i) => (
        <div key={i} style={{ background: '#F8FAFF', borderRadius: 8, padding: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1B3A8C', marginBottom: 4 }}>✍️ Writing · {e.nivel}</div>
          <div style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic', marginBottom: 6 }}>{e.pregunta}</div>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{e.texto}</div>
        </div>
      ))}

      {(examen.audios || []).map((a, i) => (
        <div key={i} style={{ background: '#F8FAFF', borderRadius: 8, padding: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1B3A8C', marginBottom: 6 }}>🎤 Speaking</div>
          {a.url
            ? <audio controls src={a.url} style={{ width: '100%' }} />
            : <div style={{ fontSize: 12, color: '#9CA3AF' }}>Audio no disponible</div>}
        </div>
      ))}

      {!soloLectura && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 10, paddingTop: 10, borderTop: '1px solid #E2E6F0', flexWrap: 'wrap' }}>
          <div>
            <label style={estilos.label}>Nivel final</label>
            <select value={nivelFinal} onChange={e => setNivelFinal(e.target.value)} style={{ ...estilos.input, width: 140 }}>
              <option value="">Elegir...</option>
              {NIVELES_ORDEN.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={estilos.label}>Notas (opcional)</label>
            <input type="text" value={notas} onChange={e => setNotas(e.target.value)} style={estilos.input} placeholder="Observaciones para el estudiante o el equipo" />
          </div>
          <button onClick={calificar} disabled={guardando} style={estilos.boton()}>
            {guardando ? 'Guardando...' : 'Guardar calificación'}
          </button>
        </div>
      )}
      {soloLectura && examen.notas_admin && (
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8, paddingTop: 8, borderTop: '1px solid #E2E6F0' }}>
          Notas: {examen.notas_admin}
        </div>
      )}
    </div>
  )
}

function TabExamenNivel({ usuario, setMensaje }) {
  const [subTab, setSubTab] = useState('pendiente')
  const [examenes, setExamenes] = useState([])
  const [cargando, setCargando] = useState(true)

  const cargar = useCallback(() => {
    setCargando(true)
    axios.get(`${API}/examen-revision/admin/listado`, { ...cabeceras(usuario), params: { estado: subTab } })
      .then(r => setExamenes(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los exámenes.' }))
      .finally(() => setCargando(false))
  }, [usuario, subTab, setMensaje])

  useEffect(() => { cargar() }, [cargar])

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <button style={estilos.tab(subTab === 'pendiente')} onClick={() => setSubTab('pendiente')}>⏳ Pendientes</button>
        <button style={estilos.tab(subTab === 'revisado')} onClick={() => setSubTab('revisado')}>✅ Revisados</button>
      </div>
      {cargando && <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>}
      {!cargando && examenes.length === 0 && (
        <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>
          {subTab === 'pendiente' ? 'No hay exámenes pendientes por calificar.' : 'Todavía no hay exámenes revisados.'}
        </div>
      )}
      {!cargando && examenes.map(ex => (
        <FilaExamen key={ex.id} examen={ex} usuario={usuario} setMensaje={setMensaje} onCalificado={cargar} />
      ))}
    </div>
  )
}

// ══════════════════════════════════════ TAB: ESTADISTICAS ═══
function StatCard({ ic, valor, label, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, padding: '20px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>{ic}</div>
      <div style={{ fontSize: 32, fontWeight: 900, color: color || '#1A1A2E' }}>{valor}</div>
      <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  )
}

function TabEstadisticas({ usuario, setMensaje }) {
  const [stats, setStats] = useState(null)
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    axios.get(`${API}/curriculo/admin/estadisticas`, cabeceras(usuario))
      .then(r => setStats(r.data.datos))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar las estadisticas.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])

  if (cargando) return <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>
  if (!stats) return null

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
      <StatCard ic="👥" valor={stats.total_usuarios} label="Estudiantes registrados" color="#1B3A8C" />
      <StatCard ic="🗓" valor={stats.total_clases} label="Clases programadas" color="#7C3AED" />
      <StatCard ic="🎙" valor={stats.total_foros} label="VideoForos programados" color="#059669" />
      <StatCard ic="🔥" valor={stats.total_retos} label="Retos activos" color="#EF4444" />
      <StatCard ic="📋" valor={stats.total_inscClases} label="Reservas a clases" color="#F59E0B" />
      <StatCard ic="🎤" valor={stats.total_inscForos} label="Inscripciones a foros" color="#3B82F6" />
    </div>
  )
}

// ══════════════════════════════════════ TAB: LECTURAS ═══════
// 3 tipos de contenido, cada uno con su propia tabla: trabalenguas
// (tongue twisters), proverbios (mindset/motivacion), y cuentos
// (historias cortas con parrafos EN/ES que otorgan XP).
const DIFICULTADES = ['Facil', 'Medio', 'Dificil']
const CATEGORIAS_PROVERBIO = ['motivacion', 'sabiduria', 'disciplina', 'exito', 'mentalidad_crecimiento']

function TabLecturas({ usuario, setMensaje }) {
  const [subTab, setSubTab] = useState('trabalenguas')
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <button style={estilos.tab(subTab === 'trabalenguas')} onClick={() => setSubTab('trabalenguas')}>🌀 Trabalenguas</button>
        <button style={estilos.tab(subTab === 'proverbios')} onClick={() => setSubTab('proverbios')}>💬 Proverbios</button>
        <button style={estilos.tab(subTab === 'cuentos')} onClick={() => setSubTab('cuentos')}>📖 Cuentos</button>
      </div>
      {subTab === 'trabalenguas' && <SubTabTrabalenguas usuario={usuario} setMensaje={setMensaje} />}
      {subTab === 'proverbios' && <SubTabProverbios usuario={usuario} setMensaje={setMensaje} />}
      {subTab === 'cuentos' && <SubTabCuentos usuario={usuario} setMensaje={setMensaje} />}
    </div>
  )
}

function SubTabTrabalenguas({ usuario, setMensaje }) {
  const [items, setItems] = useState([])
  const [cargando, setCargando] = useState(true)
  const [editando, setEditando] = useState(null)

  const cargar = useCallback(() => {
    setCargando(true)
    axios.get(`${API}/lecturas/admin/trabalenguas`, cabeceras(usuario))
      .then(r => setItems(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los trabalenguas.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])
  useEffect(() => { cargar() }, [cargar])

  async function guardar(form) {
    try {
      if (form.id) await axios.put(`${API}/lecturas/admin/trabalenguas/${form.id}`, form, cabeceras(usuario))
      else await axios.post(`${API}/lecturas/admin/trabalenguas`, form, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Trabalenguas guardado.' })
      setEditando(null); cargar()
    } catch (err) { setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar.' }) }
  }
  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este trabalenguas?')) return
    try {
      await axios.delete(`${API}/lecturas/admin/trabalenguas/${id}`, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Eliminado.' }); cargar()
    } catch { setMensaje({ tipo: 'error', texto: 'No se pudo eliminar.' }) }
  }
  function alternarActivo(item) {
    axios.put(`${API}/lecturas/admin/trabalenguas/${item.id}`, { activo: !item.activo }, cabeceras(usuario))
      .then(cargar).catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo actualizar.' }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button style={estilos.boton()} onClick={() => setEditando({ orden: items.length + 1, texto: '', traduccion: '', dificultad: 'Facil' })}>+ Nuevo trabalenguas</button>
      </div>
      {editando && (
        <div style={estilos.card}>
          <label style={estilos.label}>Texto (inglés)</label>
          <input style={estilos.input} value={editando.texto} onChange={e => setEditando({ ...editando, texto: e.target.value })} placeholder="She sells seashells by the seashore" />
          <label style={{ ...estilos.label, marginTop: 10 }}>Traducción</label>
          <input style={estilos.input} value={editando.traduccion || ''} onChange={e => setEditando({ ...editando, traduccion: e.target.value })} placeholder="Ella vende conchas de mar en la orilla" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10, marginBottom: 12 }}>
            <div>
              <label style={estilos.label}>Dificultad</label>
              <select style={estilos.input} value={editando.dificultad} onChange={e => setEditando({ ...editando, dificultad: e.target.value })}>
                {DIFICULTADES.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={estilos.label}>Orden</label>
              <input type="number" style={estilos.input} value={editando.orden} onChange={e => setEditando({ ...editando, orden: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={estilos.boton()} onClick={() => guardar(editando)} disabled={!editando.texto}>Guardar</button>
            <button style={estilos.botonSec} onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      )}
      {cargando ? <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={estilos.th}>Texto</th><th style={estilos.th}>Dificultad</th><th style={estilos.th}>Estado</th><th style={estilos.th}></th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id}>
                <td style={estilos.td}>{i.texto}</td>
                <td style={estilos.td}>{i.dificultad}</td>
                <td style={estilos.td}><span style={{ color: i.activo ? '#166534' : '#991B1B', fontWeight: 700 }}>{i.activo ? '● Activo' : '○ Inactivo'}</span></td>
                <td style={estilos.td}>
                  <button style={{ ...estilos.botonSec, marginRight: 8, padding: '5px 12px' }} onClick={() => setEditando(i)}>Editar</button>
                  <button style={{ ...estilos.botonSec, marginRight: 8, padding: '5px 12px' }} onClick={() => alternarActivo(i)}>{i.activo ? 'Desactivar' : 'Activar'}</button>
                  <button style={{ ...estilos.botonSec, padding: '5px 12px', color: '#B91C1C' }} onClick={() => eliminar(i.id)}>Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no hay trabalenguas.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  )
}

function SubTabProverbios({ usuario, setMensaje }) {
  const [items, setItems] = useState([])
  const [cargando, setCargando] = useState(true)
  const [editando, setEditando] = useState(null)

  const cargar = useCallback(() => {
    setCargando(true)
    axios.get(`${API}/lecturas/admin/proverbios`, cabeceras(usuario))
      .then(r => setItems(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los proverbios.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])
  useEffect(() => { cargar() }, [cargar])

  async function guardar(form) {
    try {
      if (form.id) await axios.put(`${API}/lecturas/admin/proverbios/${form.id}`, form, cabeceras(usuario))
      else await axios.post(`${API}/lecturas/admin/proverbios`, form, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Proverbio guardado.' })
      setEditando(null); cargar()
    } catch (err) { setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar.' }) }
  }
  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este proverbio?')) return
    try {
      await axios.delete(`${API}/lecturas/admin/proverbios/${id}`, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Eliminado.' }); cargar()
    } catch { setMensaje({ tipo: 'error', texto: 'No se pudo eliminar.' }) }
  }
  function alternarActivo(item) {
    axios.put(`${API}/lecturas/admin/proverbios/${item.id}`, { activo: !item.activo }, cabeceras(usuario))
      .then(cargar).catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo actualizar.' }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button style={estilos.boton()} onClick={() => setEditando({ orden: items.length + 1, frase: '', significado: '', categoria: 'motivacion' })}>+ Nuevo proverbio</button>
      </div>
      {editando && (
        <div style={estilos.card}>
          <label style={estilos.label}>Frase (inglés)</label>
          <input style={estilos.input} value={editando.frase} onChange={e => setEditando({ ...editando, frase: e.target.value })} placeholder="The early bird catches the worm" />
          <label style={{ ...estilos.label, marginTop: 10 }}>Significado</label>
          <input style={estilos.input} value={editando.significado || ''} onChange={e => setEditando({ ...editando, significado: e.target.value })} placeholder="Quien madruga, avanza más" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10, marginBottom: 12 }}>
            <div>
              <label style={estilos.label}>Categoría</label>
              <select style={estilos.input} value={editando.categoria} onChange={e => setEditando({ ...editando, categoria: e.target.value })}>
                {CATEGORIAS_PROVERBIO.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={estilos.label}>Orden</label>
              <input type="number" style={estilos.input} value={editando.orden} onChange={e => setEditando({ ...editando, orden: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={estilos.boton()} onClick={() => guardar(editando)} disabled={!editando.frase}>Guardar</button>
            <button style={estilos.botonSec} onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      )}
      {cargando ? <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={estilos.th}>Frase</th><th style={estilos.th}>Categoría</th><th style={estilos.th}>Estado</th><th style={estilos.th}></th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id}>
                <td style={estilos.td}>{i.frase}</td>
                <td style={estilos.td}>{i.categoria}</td>
                <td style={estilos.td}><span style={{ color: i.activo ? '#166534' : '#991B1B', fontWeight: 700 }}>{i.activo ? '● Activo' : '○ Inactivo'}</span></td>
                <td style={estilos.td}>
                  <button style={{ ...estilos.botonSec, marginRight: 8, padding: '5px 12px' }} onClick={() => setEditando(i)}>Editar</button>
                  <button style={{ ...estilos.botonSec, marginRight: 8, padding: '5px 12px' }} onClick={() => alternarActivo(i)}>{i.activo ? 'Desactivar' : 'Activar'}</button>
                  <button style={{ ...estilos.botonSec, padding: '5px 12px', color: '#B91C1C' }} onClick={() => eliminar(i.id)}>Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no hay proverbios.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  )
}

function SubTabCuentos({ usuario, setMensaje }) {
  const [items, setItems] = useState([])
  const [cargando, setCargando] = useState(true)
  const [editando, setEditando] = useState(null) // { id?, titulo, nivel, resumen, xp, orden, parrafos: [{en,es}] }
  const [cargandoDetalle, setCargandoDetalle] = useState(false)

  const cargar = useCallback(() => {
    setCargando(true)
    axios.get(`${API}/lecturas/admin/cuentos`, cabeceras(usuario))
      .then(r => setItems(r.data.datos || []))
      .catch(() => setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los cuentos.' }))
      .finally(() => setCargando(false))
  }, [usuario, setMensaje])
  useEffect(() => { cargar() }, [cargar])

  function nuevo() {
    setEditando({ titulo: '', nivel: 'A1', resumen: '', xp: 20, orden: items.length + 1, parrafos: [{ en: '', es: '' }] })
  }
  async function editar(id) {
    setCargandoDetalle(true)
    try {
      const r = await axios.get(`${API}/lecturas/admin/cuentos/${id}`, cabeceras(usuario))
      const c = r.data.datos
      setEditando({ ...c, parrafos: (c.contenido?.parrafos && c.contenido.parrafos.length > 0) ? c.contenido.parrafos : [{ en: '', es: '' }] })
    } catch { setMensaje({ tipo: 'error', texto: 'No se pudo cargar el cuento.' }) }
    setCargandoDetalle(false)
  }
  function actualizarParrafo(i, campo, valor) {
    setEditando(e => ({ ...e, parrafos: e.parrafos.map((p, idx) => idx === i ? { ...p, [campo]: valor } : p) }))
  }
  function agregarParrafo() {
    setEditando(e => ({ ...e, parrafos: [...e.parrafos, { en: '', es: '' }] }))
  }
  function quitarParrafo(i) {
    setEditando(e => ({ ...e, parrafos: e.parrafos.filter((_, idx) => idx !== i) }))
  }
  async function guardar() {
    const { id, titulo, nivel, resumen, xp, orden, parrafos } = editando
    const body = { titulo, nivel, resumen, xp: parseInt(xp) || 0, orden: parseInt(orden) || 0, contenido: { parrafos: parrafos.filter(p => p.en || p.es) } }
    try {
      if (id) await axios.put(`${API}/lecturas/admin/cuentos/${id}`, body, cabeceras(usuario))
      else await axios.post(`${API}/lecturas/admin/cuentos`, body, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Cuento guardado.' })
      setEditando(null); cargar()
    } catch (err) { setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar.' }) }
  }
  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este cuento?')) return
    try {
      await axios.delete(`${API}/lecturas/admin/cuentos/${id}`, cabeceras(usuario))
      setMensaje({ tipo: 'ok', texto: 'Eliminado.' }); cargar()
    } catch { setMensaje({ tipo: 'error', texto: 'No se pudo eliminar.' }) }
  }
  function alternarActivo(item) {
    axios.put(`${API}/lecturas/admin/cuentos/${item.id}`, { activo: !item.activo }, cabeceras(usuario))
      .then(cargar).catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo actualizar.' }))
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button style={estilos.boton()} onClick={nuevo}>+ Nuevo cuento</button>
      </div>

      {cargandoDetalle && <div style={{ textAlign: 'center', padding: 20, color: '#6B7280' }}>Cargando cuento...</div>}

      {editando && !cargandoDetalle && (
        <div style={estilos.card}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={estilos.label}>Título</label>
              <input style={estilos.input} value={editando.titulo} onChange={e => setEditando({ ...editando, titulo: e.target.value })} placeholder="The Lost Key" />
            </div>
            <div>
              <label style={estilos.label}>Nivel</label>
              <select style={estilos.input} value={editando.nivel} onChange={e => setEditando({ ...editando, nivel: e.target.value })}>
                {NIVELES_ORDEN.filter(n => n !== 'PRE').map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={estilos.label}>XP</label>
              <input type="number" style={estilos.input} value={editando.xp} onChange={e => setEditando({ ...editando, xp: e.target.value })} />
            </div>
          </div>
          <label style={estilos.label}>Resumen</label>
          <input style={estilos.input} value={editando.resumen || ''} onChange={e => setEditando({ ...editando, resumen: e.target.value })} placeholder="Una frase corta que resuma la historia" />

          <label style={{ ...estilos.label, marginTop: 10, marginBottom: 8 }}>Párrafos (inglés + traducción)</label>
          {editando.parrafos.map((p, i) => (
            <div key={i} style={{ background: '#F8FAFF', border: '1px solid #E2E6F0', borderRadius: 10, padding: 12, marginBottom: 8 }}>
              <textarea style={{ ...estilos.input, minHeight: 50, resize: 'vertical', marginBottom: 6 }} value={p.en} onChange={e => actualizarParrafo(i, 'en', e.target.value)} placeholder="Paragraph in English..." />
              <textarea style={{ ...estilos.input, minHeight: 40, resize: 'vertical', marginBottom: 6, fontStyle: 'italic' }} value={p.es} onChange={e => actualizarParrafo(i, 'es', e.target.value)} placeholder="Traducción al español..." />
              {editando.parrafos.length > 1 && (
                <button style={{ ...estilos.botonSec, padding: '4px 10px', fontSize: 11, color: '#B91C1C' }} onClick={() => quitarParrafo(i)}>Quitar párrafo</button>
              )}
            </div>
          ))}
          <button style={{ ...estilos.botonSec, marginBottom: 16 }} onClick={agregarParrafo}>+ Agregar párrafo</button>

          <div style={{ display: 'flex', gap: 8 }}>
            <button style={estilos.boton()} onClick={guardar} disabled={!editando.titulo}>Guardar</button>
            <button style={estilos.botonSec} onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {cargando ? <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={estilos.th}>Título</th><th style={estilos.th}>Nivel</th><th style={estilos.th}>XP</th><th style={estilos.th}>Estado</th><th style={estilos.th}></th></tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id}>
                <td style={estilos.td}>{i.titulo}</td>
                <td style={estilos.td}>{i.nivel}</td>
                <td style={estilos.td}>{i.xp}</td>
                <td style={estilos.td}><span style={{ color: i.activo ? '#166534' : '#991B1B', fontWeight: 700 }}>{i.activo ? '● Activo' : '○ Inactivo'}</span></td>
                <td style={estilos.td}>
                  <button style={{ ...estilos.botonSec, marginRight: 8, padding: '5px 12px' }} onClick={() => editar(i.id)}>Editar</button>
                  <button style={{ ...estilos.botonSec, marginRight: 8, padding: '5px 12px' }} onClick={() => alternarActivo(i)}>{i.activo ? 'Desactivar' : 'Activar'}</button>
                  <button style={{ ...estilos.botonSec, padding: '5px 12px', color: '#B91C1C' }} onClick={() => eliminar(i.id)}>Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} style={{ ...estilos.td, textAlign: 'center', color: '#6B7280' }}>Todavia no hay cuentos.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ══════════════════════════════════════ PAGINA PRINCIPAL ═══
export default function AdminClases({ usuario, onIniciarClase }) {
  const [tab, setTab] = useState('clases')
  const [mensaje, setMensaje] = useState(null)

  useEffect(() => {
    if (!mensaje) return
    const t = setTimeout(() => setMensaje(null), 4000)
    return () => clearTimeout(t)
  }, [mensaje])

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', animation: 'fadeIn .3s ease both' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E', marginBottom: 6 }}>🛠 Panel de administrador — Clases en vivo</h1>
      <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>
        Crea clases puntuales (fecha, hora, requisitos), administra profesores, y mira quien se ha reservado a cada clase.
      </p>

      {mensaje && (
        <div style={{ background: mensaje.tipo === 'ok' ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${mensaje.tipo === 'ok' ? '#22C55E' : '#EF4444'}`, borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: mensaje.tipo === 'ok' ? '#166534' : '#991B1B' }}>
          {mensaje.tipo === 'ok' ? '✅ ' : '⚠ '}{mensaje.texto}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <button style={estilos.tab(tab === 'estadisticas')} onClick={() => setTab('estadisticas')}>📊 Estadisticas</button>
        <button style={estilos.tab(tab === 'clases')} onClick={() => setTab('clases')}>🗓 Clases en vivo</button>
        <button style={estilos.tab(tab === 'foros')} onClick={() => setTab('foros')}>🎙 VideoForos</button>
        <button style={estilos.tab(tab === 'reservas')} onClick={() => setTab('reservas')}>📅 Reservas</button>
        <button style={estilos.tab(tab === 'lecturas')} onClick={() => setTab('lecturas')}>📖 Lecturas</button>
        <button style={estilos.tab(tab === 'videos')} onClick={() => setTab('videos')}>🎬 Videos de lecciones</button>
        <button style={estilos.tab(tab === 'profesores')} onClick={() => setTab('profesores')}>🧑‍🏫 Profesores</button>
        <button style={estilos.tab(tab === 'pagos')} onClick={() => setTab('pagos')}>💳 Pagos</button>
        <button style={estilos.tab(tab === 'estudiantes')} onClick={() => setTab('estudiantes')}>🎓 Estudiantes</button>
        <button style={estilos.tab(tab === 'examen_nivel')} onClick={() => setTab('examen_nivel')}>📝 Examen de nivel</button>
      </div>

      {tab === 'estadisticas' && <TabEstadisticas usuario={usuario} setMensaje={setMensaje} />}
      {tab === 'clases'       && <TabClases     usuario={usuario} setMensaje={setMensaje} onIniciarClase={onIniciarClase} />}
      {tab === 'foros'        && <TabVideoForos usuario={usuario} setMensaje={setMensaje} />}
      {tab === 'reservas'     && <TabReservas   usuario={usuario} setMensaje={setMensaje} />}
      {tab === 'lecturas'     && <TabLecturas   usuario={usuario} setMensaje={setMensaje} />}
      {tab === 'videos'       && <SalaVideos    usuario={usuario} />}
      {tab === 'profesores'   && <TabProfesores usuario={usuario} setMensaje={setMensaje} />}
      {tab === 'pagos'        && <TabPagos      usuario={usuario} setMensaje={setMensaje} />}
      {tab === 'estudiantes'  && <TabEstudiantes usuario={usuario} setMensaje={setMensaje} />}
      {tab === 'examen_nivel' && <TabExamenNivel usuario={usuario} setMensaje={setMensaje} />}
    </div>
  )
}
